import { PenIcon, TrashIcon } from "@xola/icons";
import { Avatar, Badge, flash, Logo, Switch } from "@xola/ui-kit";
import clsx from "clsx";
import React from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { Seller, TelemetryModule, TelemetryModuleConfiguration, TelemetryParameterType } from "types";
import { api } from "../../api/api";
import { xolaUri } from "../../api/xolaUri";
import { useSeller } from "../../hooks/useAccount";
import { SecondaryButton } from "../Common/SecondaryButton";

const getConfiguration = (seller: Seller, module: TelemetryModule) => {
    const defaultConfiguration = {
        enabled: false,
        module: {
            id: module.id ?? "",
        },
        parameters: module.parameters.map((parameter) => {
            return {
                key: parameter.key,
                value: parameter?.value,
            };
        }),
    };
    if (!seller.preferences.telemetry) {
        return defaultConfiguration;
    }

    const configuration = seller.preferences.telemetry.modules?.find((config) => config.module.id === module.id);
    return configuration ?? defaultConfiguration;
};

const getIsEnableable = (configuration: TelemetryModuleConfiguration, module: TelemetryModule) => {
    let isEnableable = true;

    for (const parameter of configuration.parameters) {
        const moduleParameter = module.parameters.find((p) => p.key === parameter.key);
        const isRequired =
            moduleParameter?.type === TelemetryParameterType.STRING ||
            moduleParameter?.type === TelemetryParameterType.SECRET;
        if (!moduleParameter || (isRequired && !parameter.value)) {
            isEnableable = false;
        }
    }

    return isEnableable;
};

interface TelemetryModuleItemProps {
    telemetryModule: TelemetryModule;
    onDelete: (moduleId: string) => Promise<void>;
}

export const TelemetryModuleItem = ({ telemetryModule, onDelete }: TelemetryModuleItemProps) => {
    const history = useHistory();
    const seller = useSeller();
    const configuration = getConfiguration(seller, telemetryModule);
    const isEnableable = getIsEnableable(configuration, telemetryModule);

    const handleModuleEdit = () => {
        history.push(`/marketing/serverSideTracking/modules/${telemetryModule.id}/edit`);
    };

    const handleModuleDelete = (e: Event) => {
        e.stopPropagation();
        if (telemetryModule.id) {
            onDelete(telemetryModule.id);
        }
    };

    const handleToggleEnable = () => {
        configuration.enabled = !configuration.enabled;
        saveConfiguration(configuration);
    };

    const { mutateAsync: saveConfiguration, isLoading: isSavingConfiguration } = useMutation(
        async (values: TelemetryModuleConfiguration) => {
            const url = `/sellers/${seller.id}/preferences/telemetry/configurations`;
            await (values.id ? api.put(`${url}/${values.id}`, values) : api.post(url, values));
            const action = values.enabled ? "enabled" : "disabled";
            flash.success(`Successfully ${action} integration`);
        },
    );

    const pictureSuffix = String(telemetryModule.updatedAt?.unix() ?? telemetryModule.id);
    const pictureSource = telemetryModule?.picture?.id
        ? `${xolaUri}/api/telemetryModules/${telemetryModule.id}/picture?size=medium&cache_key=${pictureSuffix}`
        : undefined;

    return (
        <div
            className={clsx(
                "flex cursor-pointer items-center overflow-hidden border-b border-b-gray-light px-5 py-4.5 hover:bg-gray-lighter xl:px-10",
            )}
        >
            <Switch.Group>
                <Switch
                    isChecked={configuration.enabled}
                    size="large"
                    disabled={!isEnableable || isSavingConfiguration}
                    onChange={handleToggleEnable}
                />
            </Switch.Group>
            {pictureSource ? (
                <Logo src={pictureSource} className="mr-3 ml-3 h-12 w-12 rounded-full" />
            ) : (
                <Avatar color="bg-primary-lighter" name={telemetryModule.name} size="medium" className="mr-3 ml-3" />
            )}

            <div className="mr-5">
                <h3 className="truncate text-lg font-bold leading-p2 tracking-tightest">{telemetryModule.name}</h3>
            </div>
            {telemetryModule?.seller ? <Badge color="warning">Custom</Badge> : null}
            <div className="flex flex-auto justify-end space-x-2">
                <SecondaryButton data-testid="telemetry-item-edit-button" size="small" onClick={handleModuleEdit}>
                    <PenIcon />
                </SecondaryButton>
                {telemetryModule.seller && telemetryModule.id ? (
                    <SecondaryButton
                        data-testid="telemetry-item-remove-button"
                        size="small"
                        onClick={handleModuleDelete}
                    >
                        <TrashIcon />
                    </SecondaryButton>
                ) : null}
            </div>
        </div>
    );
};
